// Traduz a paleta e a arte de uma região nas custom properties que o CSS consome.
// Função pura, sem React e sem conhecer bioma nenhum: recebe o descritor, devolve
// um objeto de `style`.
//
// POR QUE PELO JS, E NÃO POR UM BLOCO `[data-tl-region="..."]` NO CSS
// -------------------------------------------------------------------
// Com bloco por região, acrescentar região seria editar CSS — e a promessa do
// módulo é que seja encher uma pasta. Pior: a URL da arte tem de vir do `import`,
// que é o que o webpack sabe versionar e emitir; escrita à mão no CSS ela viraria
// um caminho relativo que o build não acompanha.
//
// Aqui o CSS só declara os valores da Caatinga como PADRÃO (ela foi a primeira
// região, e as primitivas dela já estavam lá) e consome as variáveis. Quem passa
// uma região sobrescreve; quem não passa, cai no que o CSS tem.
//
// POR QUE PROP, E NÃO CONTEXTO REACT
// ----------------------------------
// O núcleo é portável por CONTRATO: quem plugar a trilha noutro host escreve um
// irmão de `ava/` e o núcleo não muda. Um contexto seria um canal invisível entre
// as duas camadas, e a próxima pessoa descobriria que precisa provê-lo pelo
// sintoma — um tabuleiro sem cenário. Prop falha na cara de quem esquecer.

/**
 * As custom properties de uma região, prontas para `style`.
 *
 * @param {object} [regiao] descritor; sem ele devolve `{}` e o CSS manda
 * @returns {object} objeto de estilo React com as `--tl-*` da região
 */
export function variaveisDaRegiao(regiao) {
  if (!regiao) return {};

  const estilo = {};
  const { cores, tabuleiro } = regiao;

  if (tabuleiro) {
    // `url("...")` montado aqui, e não no CSS: o valor que o `import` devolve é a
    // URL final que o webpack emitiu, com o hash do arquivo.
    if (tabuleiro.fundo) estilo["--tl-cenario"] = `url("${tabuleiro.fundo}")`;
    if (tabuleiro.fundoDesfocado) {
      estilo["--tl-cenario-desfocado"] = `url("${tabuleiro.fundoDesfocado}")`;
    }
    // A ilustração de fundo das etapas de escolher explorador e de apresentação do
    // mascote. Fica em `tabuleiro` e não em `cores` porque é ARTE, como as outras
    // duas — mesmo não sendo do tabuleiro em si.
    if (tabuleiro.fundoSelecao) {
      estilo["--tl-fundo-selecao"] = `url("${tabuleiro.fundoSelecao}")`;
    }
    // A CENA do painel da região na coleção de cartas. Mesma natureza das de cima, e
    // é aqui que ela vira `background` — o CSS não pode escrever a URL à mão, porque
    // o que o `import` devolve é o caminho com o hash que o webpack emitiu.
    if (tabuleiro.fundoColecao) {
      estilo["--tl-colecao-cena"] = `url("${tabuleiro.fundoColecao}")`;
    }
  }

  if (cores) {
    if (cores.region) estilo["--tl-color-region"] = cores.region;
    if (cores.regionStrong) {
      estilo["--tl-color-region-strong"] = cores.regionStrong;
    }
    if (cores.fundoTopo) estilo["--tl-color-fundo-topo"] = cores.fundoTopo;
    if (cores.fundoMeio) estilo["--tl-color-fundo-meio"] = cores.fundoMeio;
    if (cores.fundoBase) estilo["--tl-color-fundo-base"] = cores.fundoBase;
    // O véu sobre a ilustração embaçada do `::before`. Tem ALFA, então não sai dos
    // três de cima: custom property guarda a cor inteira, e aplicar transparência
    // sobre ela pediria `color-mix()`, que este AVA não alcança. Opcionais — sem
    // eles o CSS usa o véu da Caatinga.
    if (cores.veuTopo) estilo["--tl-veu-topo"] = cores.veuTopo;
    if (cores.veuMeio) estilo["--tl-veu-meio"] = cores.veuMeio;
    if (cores.veuBase) estilo["--tl-veu-base"] = cores.veuBase;
    // O cromo: setas de navegação e pílula do contador. Separado do `region*` de
    // propósito — o accent aparece sobre o degradê da casca (borda do explorador,
    // região no mapa), e o cromo fica sobre o CENÁRIO, que é outra vizinhança. No
    // Norte os dois divergem: o accent é o ouro da onça e o cromo é verde-floresta.
    if (cores.chromeBorda)
      estilo["--tl-color-chrome-borda"] = cores.chromeBorda;
    if (cores.chromeTexto)
      estilo["--tl-color-chrome-texto"] = cores.chromeTexto;
    if (cores.chromeFundo)
      estilo["--tl-color-chrome-fundo"] = cores.chromeFundo;
    // O véu radial sobre a ilustração da seleção de explorador. Alfa, então valores
    // próprios — igual ao véu do tabuleiro.
    if (cores.veuSelecaoCentro) {
      estilo["--tl-veu-selecao-centro"] = cores.veuSelecaoCentro;
    }
    if (cores.veuSelecaoMeio) {
      estilo["--tl-veu-selecao-meio"] = cores.veuSelecaoMeio;
    }
    if (cores.veuSelecaoBorda) {
      estilo["--tl-veu-selecao-borda"] = cores.veuSelecaoBorda;
    }
  }

  return estilo;
}

export default variaveisDaRegiao;
